

'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import Image from '@/components/ui/Image';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Shield, Users, Crown, Calendar, Info, Music, Palette, ListMusic, Save, Trash2, PlusCircle, Newspaper, ImageIcon, Handshake, Check, X, Edit, FileSignature, AlignLeft, Settings, Star, Pin } from 'lucide-react';
import { MediaUploader } from '@/components/social/MediaUploader';
import { SongPicker } from '@/components/social/SongPicker';
import { MentionTextarea } from '@/components/social/MentionTextarea';
import { useToast } from "@/hooks/use-toast";
import { createBandaSocialPost, createBandaSocialStory, acceptCollaboration, rejectCollaboration, deleteSocialPost, createContract, signContract, rejectContract, deleteSocialStory, saveBandaSettings, getArchivedStories, createHighlightStoryGroup, deleteHighlightStoryGroup } from '@/lib/actions';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Banda, UserProfile, SocialPost, SocialStory, JartinetHermandad, Contract, HighlightStoryGroup } from "@/lib/types";
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { useMemoFirebase, useAuth } from '@/firebase/index';
import { db as firestore } from '@/firebase/config';
import { useCollection } from '@/firebase/firestore/use-collection';
import { collection, query, where, doc } from 'firebase/firestore';
import { useDoc } from '@/firebase/firestore/use-doc';
import { ContractCard } from '../contratos/ContractCard';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';

export const dynamic = 'force-dynamic';

function PinPostsForm({ banda, posts, onFormSuccess }: { banda: Banda; posts: SocialPost[], onFormSuccess: () => void }) {
    const [pinned, setPinned] = useState(banda.pinnedPosts || { left: undefined, center: undefined, right: undefined });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSelectorOpen, setSelectorOpen] = useState(false);
    const [currentSlot, setCurrentSlot] = useState<'left' | 'center' | 'right' | null>(null);
    const { toast } = useToast();

    const handleSelectClick = (slot: 'left' | 'center' | 'right') => {
        setCurrentSlot(slot);
        setSelectorOpen(true);
    };

    const handlePostSelect = (postId: string | null) => {
        if (currentSlot) {
            setPinned(prev => ({ ...prev, [currentSlot]: postId }));
        }
        setSelectorOpen(false);
        setCurrentSlot(null);
    };
    
    const handleSave = async () => {
        setIsSubmitting(true);
        const result = await saveBandaSettings(banda.id, { pinnedPosts: pinned });
        if (result.success) {
            toast({ title: 'Éxito', description: 'Publicaciones fijadas actualizadas.' });
            onFormSuccess();
        } else {
            toast({ title: 'Error', description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    const renderSlot = (slot: 'left' | 'center' | 'right') => {
        const postId = pinned[slot];
        const post = postId ? posts.find(p => p.id === postId) : null;
        const mediaUrl = post ? (Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl) : null;
        
        return (
             <Card>
                <CardHeader>
                    <CardTitle className="capitalize text-lg">{slot}</CardTitle>
                </CardHeader>
                <CardContent className="h-24 flex items-center justify-center bg-muted/50 rounded-md">
                     {post ? (
                        <div className="flex items-center gap-2 text-sm">
                            {mediaUrl && <Image src={mediaUrl} alt={post.title} width={40} height={40} className="rounded-sm aspect-square object-cover" />}
                            <span className="line-clamp-2 font-semibold">{post.title}</span>
                        </div>
                    ) : (
                        <p className="text-xs text-muted-foreground">Vacío</p>
                    )}
                </CardContent>
                <CardFooter className="pt-4">
                    <Button variant="outline" className="w-full" onClick={() => handleSelectClick(slot)}>Cambiar</Button>
                </CardFooter>
            </Card>
        );
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {renderSlot('left')}
                {renderSlot('center')}
                {renderSlot('right')}
            </div>
            <div className="flex justify-end">
                <Button onClick={handleSave} disabled={isSubmitting}>
                    {isSubmitting && <Loader2 className="animate-spin mr-2"/>}
                    Guardar Publicaciones Fijadas
                </Button>
            </div>
             <Dialog open={isSelectorOpen} onOpenChange={setSelectorOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Seleccionar Publicación</DialogTitle>
                        <DialogDescription>Elige la publicación que quieres fijar en la posición '{currentSlot}'.</DialogDescription>
                    </DialogHeader>
                    <ScrollArea className="h-96 -mx-6 pr-2">
                        <div className="px-6 space-y-2">
                             <button onClick={() => handlePostSelect(null)} className="w-full p-3 rounded-md text-left bg-destructive/10 hover:bg-destructive/20 border border-destructive">
                                <p className="font-semibold">Quitar publicación fijada</p>
                            </button>
                            {posts.map(post => {
                                const mediaUrl = Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl;
                                return (
                                    <button key={post.id} onClick={() => handlePostSelect(post.id)} className="w-full p-3 rounded-md text-left hover:bg-accent flex items-center gap-4">
                                        {mediaUrl && <Image src={mediaUrl} alt={post.title} width={48} height={48} className="rounded-md aspect-square object-cover"/>}
                                        <div className="flex-grow">
                                            <p className="font-semibold line-clamp-1">{post.title}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-2">{post.content}</p>
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SettingsManager({ banda, onActionSuccess }: { banda: Banda, onActionSuccess: () => void }) {
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const handleSave = async (updates: Partial<Banda>) => {
        setIsSubmitting(true);
        const result = await saveBandaSettings(banda.id, updates);
        if (result.success) {
            toast({ title: 'Éxito', description: result.message });
            onActionSuccess();
        } else {
            toast({ title: 'Error', description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <div className="space-y-8">
            <UpdateColorsForm initialData={banda} onSave={handleSave} isSubmitting={isSubmitting} />
            <UpdateInfoForm initialData={banda} onSave={handleSave} isSubmitting={isSubmitting} />
            <UpdateRepertorioForm initialData={banda} onSave={handleSave} isSubmitting={isSubmitting} />
        </div>
    );
}

function UpdateRepertorioForm({ initialData, onSave, isSubmitting }: { initialData: Banda, onSave: (updates: Partial<Banda>) => void, isSubmitting: boolean }) {
    const [repertorio, setRepertorio] = useState(initialData.repertorio || []);
    const [newMarcha, setNewMarcha] = useState('');

    const handleAddMarcha = () => {
        if (newMarcha.trim() && !repertorio.includes(newMarcha.trim())) {
            setRepertorio([...repertorio, newMarcha.trim()].sort((a,b) => a.localeCompare(b)));
            setNewMarcha('');
        }
    };
    
    const handleRemoveMarcha = (marcha: string) => {
        setRepertorio(repertorio.filter(m => m !== marcha));
    };
    
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({ repertorio });
    };

    return (
        <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><ListMusic /> Repertorio Musical</CardTitle></CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-2">
                        <Input 
                            value={newMarcha}
                            onChange={(e) => setNewMarcha(e.target.value)}
                            placeholder="Nombre de la marcha"
                        />
                        <Button type="button" onClick={handleAddMarcha}><PlusCircle/>Añadir</Button>
                    </div>
                    {repertorio.length > 0 && (
                        <ScrollArea className="h-48 border rounded-md p-2">
                             <ul className="space-y-1">
                                {repertorio.map(marcha => (
                                    <li key={marcha} className="flex items-center justify-between text-sm p-1 rounded hover:bg-muted/50">
                                        <span>{marcha}</span>
                                        <Button variant="ghost" size="icon" className="h-6 w-6" type="button" onClick={() => handleRemoveMarcha(marcha)}>
                                            <Trash2 className="h-4 w-4 text-destructive"/>
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        </ScrollArea>
                    )}
                </CardContent>
                <CardFooter className="justify-end">
                     <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Guardar Repertorio
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
}


function UpdateInfoForm({ initialData, onSave, isSubmitting }: { initialData: Banda, onSave: (updates: Partial<Banda>) => void, isSubmitting: boolean }) {
    const [info, setInfo] = useState(initialData.info || '');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({ info });
    };

    return (
        <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Info/> Información</CardTitle></CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent>
                    <Label htmlFor="info">Edita la información pública del perfil de la banda.</Label>
                     <RichTextarea
                        id="info"
                        value={info}
                        onChange={(e) => setInfo(e.target.value)}
                        rows={8}
                        placeholder="Escribe aquí la historia, curiosidades o cualquier información relevante sobre la banda."
                    />
                    <p className="text-xs text-muted-foreground mt-1">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato (negrita, cursiva...).</p>
                </CardContent>
                <CardFooter className="justify-end">
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Guardar Información
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
}

function UpdateColorsForm({ initialData, onSave, isSubmitting }: { initialData: Banda, onSave: (updates: Partial<Banda>) => void, isSubmitting: boolean }) {
    const [colorPrimario, setColorPrimario] = useState(initialData.colorPrimario);
    const [colorSecundario, setColorSecundario] = useState(initialData.colorSecundario);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave({ colorPrimario, colorSecundario });
    };

    return (
        <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Palette/> Colores Corporativos</CardTitle></CardHeader>
            <form onSubmit={handleSubmit}>
                <CardContent className="flex items-center gap-8">
                    <div className="space-y-2">
                        <Label htmlFor="colorPrimario">Color Primario</Label>
                        <Input type="color" id="colorPrimario" value={colorPrimario} onChange={(e) => setColorPrimario(e.target.value)} className="p-1 h-12" />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="colorSecundario">Color Secundario</Label>
                        <Input type="color" id="colorSecundario" value={colorSecundario} onChange={(e) => setColorSecundario(e.target.value)} className="p-1 h-12" />
                    </div>
                </CardContent>
                <CardFooter className="justify-end">
                     <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Guardar Colores
                    </Button>
                </CardFooter>
            </form>
        </Card>
    );
}

function CreateHighlightGroupForm({ banda, onFormSuccess }: { banda: Banda, onFormSuccess: () => void }) {
    const { toast } = useToast();
    const formRef = useRef<HTMLFormElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [archivedStories, setArchivedStories] = useState<SocialStory[]>([]);
    const [isLoadingStories, setIsLoadingStories] = useState(true);
    const [selectedStoryIds, setSelectedStoryIds] = useState<string[]>([]);

    useEffect(() => {
        async function loadStories() {
            setIsLoadingStories(true);
            const { stories, error } = await getArchivedStories(banda.id);
            if (error) {
                toast({ title: "Error", description: "No se pudieron cargar las historias archivadas.", variant: "destructive" });
            } else {
                setArchivedStories(stories);
            }
            setIsLoadingStories(false);
        }
        loadStories();
    }, [banda.id, toast]);

    const handleStorySelection = (storyId: string, checked: boolean) => {
        setSelectedStoryIds(prev =>
            checked ? [...prev, storyId] : prev.filter(id => id !== storyId)
        );
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (selectedStoryIds.length === 0) {
            toast({ title: "Error", description: "Debes seleccionar al menos una historia.", variant: "destructive" });
            return;
        }
        setIsSubmitting(true);
        const formData = new FormData(event.currentTarget);
        
        const result = await createHighlightStoryGroup(banda.id, 'banda', formData, selectedStoryIds);
        
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            formRef.current?.reset();
            setSelectedStoryIds([]);
            onFormSuccess();
        } else {
            toast({ title: "Error", description: result.message, variant: 'destructive' });
        }
        setIsSubmitting(false);
    };

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label htmlFor="title">Título de la Destacada</Label>
                <Input id="title" name="title" required />
            </div>
            <div className="space-y-2">
                <Label htmlFor="coverUrl">URL de la Portada (Imgur)</Label>
                <Input id="coverUrl" name="coverUrl" required placeholder="https://i.imgur.com/..." />
            </div>
            <div className="space-y-2">
                <Label>Selecciona Historias</Label>
                {isLoadingStories ? <Loader2 className="animate-spin" /> : (
                    <ScrollArea className="h-48 border rounded-md p-2">
                        <div className="grid grid-cols-3 gap-2">
                            {archivedStories.map(story => (
                                <div key={story.id} className="relative aspect-square">
                                    <Image src={Array.isArray(story.mediaUrl) ? story.mediaUrl[0] : story.mediaUrl} alt="Historia" className="object-cover w-full h-full rounded-md" />
                                    <Checkbox
                                        className="absolute top-1 right-1 bg-background/50 border-white"
                                        onCheckedChange={(checked) => handleStorySelection(story.id, !!checked)}
                                    />
                                </div>
                            ))}
                        </div>
                         {archivedStories.length === 0 && <p className="text-center text-muted-foreground text-sm p-4">No hay historias archivadas para esta banda.</p>}
                    </ScrollArea>
                )}
            </div>
            <DialogFooter>
                <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
                <Button type="submit" disabled={isSubmitting || isLoadingStories}>
                    {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Crear Grupo
                </Button>
            </DialogFooter>
        </form>
    );
}

function CreateContractForm({ emitter, allHermandades, allBandas, onFormSuccess }: { emitter: {id: string, type: string, name: string}, allHermandades: JartinetHermandad[], allBandas: Banda[], onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await createContract(formData);
    
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 p-1">
      <input type="hidden" name="emitterId" value={`${emitter.type}:${emitter.id}`} />
      <div className="space-y-2">
        <Label htmlFor="concept">Concepto</Label>
        <Input id="concept" name="concept" placeholder="Ej: Acompañamiento Musical Domingo de Ramos" />
      </div>
       <div className="space-y-2">
        <Label htmlFor="recipientId">Destinatario</Label>
         <Select name="recipientId">
            <SelectTrigger><SelectValue placeholder="Elige un perfil..." /></SelectTrigger>
            <SelectContent>
                {allHermandades.map(h => (
                    <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                        {h.nombreCompleto} (Hermandad)
                      </div>
                    </SelectItem>
                ))}
                {allBandas.filter(b => b.id !== emitter.id).map(b => (
                    <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                      <div className="flex items-center gap-2">
                         <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                         {b.nombreCompleto} (Banda)
                      </div>
                    </SelectItem>
                ))}
            </SelectContent>
         </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="content">Contenido del Contrato</Label>
        <RichTextarea id="content" name="content" placeholder="Escribe aquí las cláusulas del contrato..." rows={10} />
         <p className="text-xs text-muted-foreground">Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link> para dar formato (negrita, cursiva...)</p>
      </div>
      <DialogFooter>
        <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
         <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Enviar Contrato
        </Button>
      </DialogFooter>
    </form>
  )
}

function CreatePostForm({ bandaId, onFormSuccess, allHermandades, allBandas }: { bandaId: string, onFormSuccess: () => void, allHermandades: JartinetHermandad[], allBandas: Banda[] }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const otherHermandades = allHermandades;
  const otherBandas = allBandas.filter(b => b.id !== bandaId);
  
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await createBandaSocialPost(bandaId, {}, formData);
    
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };

  return (
    <form id="create-post-form" ref={formRef} onSubmit={handleSubmit} className="space-y-4 p-1">
        <Input type="hidden" name="authorType" value="banda" />
        <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input id="title" name="title" />
        </div>
        <div className="space-y-2">
            <Label htmlFor="content">Contenido</Label>
            <MentionTextarea id="content" name="content" placeholder="Escribe aquí el contenido... usa @ para mencionar" />
             <p className="text-xs text-muted-foreground">Usa <strong>@</strong> para mencionar cuentas. Puedes usar <Link to="https://www.markdownguide.org/basic-syntax/" target="_blank" className="underline">Markdown</Link>.</p>
        </div>
        <SongPicker />
        <MediaUploader label="Fotos de la publicación" max={10} />
        <div className="space-y-2">
            <Label htmlFor="collaboratorId">Invitar a colaborar (Opcional)</Label>
            <Select name="collaboratorId">
                <SelectTrigger><SelectValue placeholder="Elige un perfil..." /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="none">No colaborar</SelectItem>
                    {otherHermandades.map(h => (
                        <SelectItem key={`hermandad:${h.id}`} value={`hermandad:${h.id}`}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-5 w-5"><AvatarImage src={h.escudoUrl} /></Avatar>
                            {h.nombreCompleto} (Hermandad)
                          </div>
                        </SelectItem>
                    ))}
                    {otherBandas.map(b => (
                        <SelectItem key={`banda:${b.id}`} value={`banda:${b.id}`}>
                          <div className="flex items-center gap-2">
                             <Avatar className="h-5 w-5"><AvatarImage src={b.escudoUrl} /></Avatar>
                             {b.nombreCompleto} (Banda)
                          </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
         <DialogFooter className="flex-shrink-0 border-t pt-4">
            <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publicar
            </Button>
         </DialogFooter>
    </form>
  )
}

function CreateStoryForm({ bandaId, onFormSuccess }: { bandaId: string, onFormSuccess: () => void }) {
  const { toast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const result = await createBandaSocialStory(bandaId, {}, formData);
    
    if (result.success) {
      toast({ title: "Éxito", description: result.message });
      formRef.current?.reset();
      onFormSuccess();
    } else {
      toast({ title: "Error", description: result.message, variant: 'destructive' });
    }
    setIsSubmitting(false);
  };
  
  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <MediaUploader single required label="Foto de la historia" />
       <div className="space-y-2">
        <Label htmlFor="text">Texto (Opcional)</Label>
        <RichTextarea id="text" name="text" placeholder="Añade un texto a tu historia..." />
      </div>
      <SongPicker />
      <DialogFooter>
        <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Publicar Historia
        </Button>
      </DialogFooter>
    </form>
  );
}

function BandaDetail({ 
    banda, 
    open, 
    onOpenChange,
    allUserProfiles,
    allHermandades,
    allBandas,
    allPosts,
    stories,
    currentUser,
    onAction,
}: { 
    banda: Banda | null, 
    open: boolean, 
    onOpenChange: (open: boolean) => void,
    allUserProfiles: Record<string, UserProfile>,
    allHermandades: JartinetHermandad[],
    allBandas: Banda[],
    allPosts: SocialPost[],
    stories: SocialStory[],
    currentUser: { uid: string } | null,
    onAction: () => void,
}) {
  const { userData } = useAuth();
  const [activeTab, setActiveTab] = useState<'info' | 'repertorio' | 'posts' | 'stories' | 'collaborations' | 'contracts' | 'destacadas' | 'fijadas' | 'settings'>('info');
  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const [isCreateStoryOpen, setIsCreateStoryOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [isCreateContractOpen, setIsCreateContractOpen] = useState(false);
  const [repertorioSearch, setRepertorioSearch] = useState('');
  const { toast } = useToast();

  const isAdmin = userData?.role === 'admin';

  const isDirector = useMemo(() => {
    if (!banda || !currentUser) return false;
    return (Array.isArray(banda.directores) && banda.directores.includes(currentUser.uid)) || isAdmin;
  }, [banda, currentUser, isAdmin]);

  const directores = (Array.isArray(banda?.directores) ? banda!.directores : []).map(uid => allUserProfiles[uid]).filter(Boolean) || [];

  const bandaCompositeId = banda ? `banda:${banda.id}` : null;

  const contractsQuery = useMemoFirebase(() => {
    if (!firestore || !bandaCompositeId || !isDirector) return null;
    return query(collection(firestore, 'contracts'), where('emitterId', '==', bandaCompositeId));
  }, [bandaCompositeId, isDirector]);

  const contractsReceivedQuery = useMemoFirebase(() => {
    if (!firestore || !bandaCompositeId || !isDirector) return null;
    return query(collection(firestore, 'contracts'), where('recipientId', '==', bandaCompositeId));
  }, [bandaCompositeId, isDirector]);

  const { data: sentContracts, isLoading: isLoadingSent } = useCollection<Contract>(contractsQuery);
  const { data: receivedContracts, isLoading: isLoadingReceived } = useCollection<Contract>(contractsReceivedQuery);

  useEffect(() => {
    if (open && banda) {
      setActiveTab('info');
    }
  }, [banda, open]);
  
  const posts = useMemo(() => {
    if (!banda) return [];
    return allPosts.filter(p => p.authorId === banda.id && p.authorType === 'banda')
                   .sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [allPosts, banda]);

  const pendingCollaborations = useMemo(() => {
    if (!banda || !allPosts) return [];
    return allPosts.filter(p => p.collaboratorId === `banda:${banda.id}` && p.collaborationStatus === 'pending');
  }, [allPosts, banda]);
  
  const sortedRepertorio = useMemo(() => {
    if (!banda || !banda.repertorio) return [];
    return [...banda.repertorio].sort((a, b) => a.localeCompare(b));
  }, [banda]);
  
  if (!banda || !currentUser) return null;
  
  const handleGenericAction = async (action: () => Promise<any>) => {
    setIsSubmitting(true);
    try {
        const result = await action();
        if (result.success) {
            toast({ title: "Éxito", description: result.message });
            onAction(); 
        } else {
            toast({ title: "Error", description: result.error || result.message, variant: "destructive" });
        }
    } catch (e: any) {
        toast({ title: "Error Inesperado", description: e.message, variant: "destructive" });
    } finally {
        setIsSubmitting(false);
    }
  }

  const handleCollaborationAction = async (action: 'accept' | 'reject', postId: string) => {
    const actionFunc = action === 'accept' ? acceptCollaboration : rejectCollaboration;
    await handleGenericAction(() => actionFunc(postId));
  };
  
  const handleDeletePost = (postId: string) => {
    if (!banda) return;
    handleGenericAction(() => deleteSocialPost(postId, banda.id, 'banda'));
  }
  
  const handleDeleteStory = (storyId: string) => {
    handleGenericAction(() => deleteSocialStory(storyId));
  }

  const handleDeleteHighlight = (highlightId: string) => {
    if (!banda) return;
    handleGenericAction(() => deleteHighlightStoryGroup(banda.id, 'banda', highlightId));
  };


  const handleSignContract = (contractId: string) => handleGenericAction(() => signContract(contractId, `banda:${banda.id}`));
  const handleRejectContract = (contractId: string) => handleGenericAction(() => rejectContract(contractId, `banda:${banda.id}`));

  const hexToRgba = (hex: string, alpha: number): string => {
    if (!hex?.startsWith('#')) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const foto = banda.foto || "https://i.imgur.com/kYkL52G.png";

  const color = banda.colorPrimario || '#9b8854';
  const colorSec = banda.colorSecundario || color;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl w-full flex flex-col p-0 gap-0">

        {/* ── Hero Header ── */}
        <div className="relative flex-shrink-0 overflow-hidden min-h-[250px] flex flex-col justify-end">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${foto}')` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
            <div className="absolute inset-0 opacity-25" style={{ background: `linear-gradient(145deg, ${color} 0%, transparent 55%)` }} />
          </div>
          <div className="relative z-10 flex flex-col items-center gap-3 text-center px-6 pb-5 pt-10">
            {/* Escudo in ring */}
            <div className="relative">
              <div className="absolute inset-0 blur-2xl rounded-full scale-[2.2] opacity-40" style={{ backgroundColor: color }} />
              <div className="relative rounded-2xl p-2 border-2 backdrop-blur-sm" style={{ borderColor: hexToRgba(color, 0.45), backgroundColor: hexToRgba(color, 0.2) }}>
                <Image src={banda.escudoUrl} alt={banda.nombreCompleto} width={80} height={80} className="h-20 w-20 object-contain drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)]" />
              </div>
            </div>
            <SheetHeader className="p-0 m-0">
              <SheetTitle className="font-headline text-xl font-bold text-foreground drop-shadow-sm leading-tight">{banda.nombreCompleto}</SheetTitle>
              <SheetDescription className="text-sm text-muted-foreground/70 -mt-1">{banda.tipo}</SheetDescription>
            </SheetHeader>
            {/* Badges */}
            <div className="flex flex-wrap gap-1.5 justify-center">
              <span className="text-[0.68rem] font-medium px-2.5 py-0.5 rounded-full border backdrop-blur-sm" style={{ borderColor: hexToRgba(color, 0.5), backgroundColor: hexToRgba(color, 0.25) }}>
                {banda.tipo}
              </span>
              {banda.fundacion && (
                <span className="text-[0.68rem] font-medium px-2.5 py-0.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm">
                  Fundada en {banda.fundacion}
                </span>
              )}
              {banda.componentes != null && (
                <span className="text-[0.68rem] font-medium px-2.5 py-0.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm">
                  {banda.componentes} componentes
                </span>
              )}
            </div>
          </div>
          {/* Two-color accent bar */}
          <div className="relative z-10 flex h-[3px] flex-shrink-0">
            <div style={{ backgroundColor: color, flex: 1 }} />
            <div style={{ backgroundColor: colorSec, flex: 1 }} />
          </div>
        </div>

        {/* ── Icon tabs ── */}
        <div className="flex-shrink-0 border-b border-white/5 bg-background/60 backdrop-blur-sm">
          <TooltipProvider delayDuration={300}>
            <div className="flex gap-0.5 px-3 py-2 flex-wrap">
              {([
                { id: 'info',           label: 'Info',          icon: Info },
                { id: 'repertorio',     label: 'Repertorio',    icon: ListMusic },
                ...(isDirector ? [
                  { id: 'posts',          label: 'Publicaciones', icon: Newspaper },
                  { id: 'stories',        label: 'Historias',     icon: ImageIcon },
                  { id: 'contracts',      label: 'Contratos',     icon: FileSignature },
                  { id: 'collaborations', label: 'Colabs',        icon: Handshake, badge: pendingCollaborations.length },
                  { id: 'destacadas',     label: 'Destacadas',    icon: Star },
                  { id: 'fijadas',        label: 'Fijadas',       icon: Pin },
                  { id: 'settings',       label: 'Ajustes',       icon: Settings },
                ] : []),
              ] as { id: string; label: string; icon: React.ElementType; badge?: number }[]).map(({ id, label, icon: Icon, badge }) => {
                const isActive = activeTab === id;
                return (
                  <Tooltip key={id}>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setActiveTab(id as any)}
                        className={cn(
                          "relative flex items-center justify-center h-9 w-9 rounded-xl transition-all duration-200 flex-shrink-0",
                          isActive ? "text-white shadow-md" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                        )}
                        style={isActive ? { backgroundColor: color } : {}}
                      >
                        <Icon className="h-4 w-4" />
                        {badge != null && badge > 0 && (
                          <span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
                            {badge}
                          </span>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {label}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        </div>
        
        <ScrollArea className="flex-grow relative">
           <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage: `url(${banda.escudoUrl})`,
                backgroundSize: '120px',
                backgroundRepeat: 'repeat',
              }}
            />
          <div className="relative z-10 p-6">
           {activeTab === 'info' && (
              <div className="space-y-4">
                {/* Stat pills */}
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { label: 'Fundación', value: banda.fundacion, icon: Calendar },
                    { label: 'Componentes', value: banda.componentes ?? '—', icon: Users },
                    { label: 'Directores', value: directores.filter(Boolean).length, icon: Crown },
                  ].map(({ label, value, icon: Icon }) => (
                    <div key={label} className="rounded-2xl border p-3 text-center" style={{ borderColor: hexToRgba(color, 0.18), background: hexToRgba(color, 0.1) }}>
                      <Icon className="h-4 w-4 mx-auto mb-1.5 opacity-60" />
                      <p className="text-lg font-bold leading-none">{value}</p>
                      <p className="text-[0.65rem] text-muted-foreground mt-1">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Directores chips */}
                {directores.filter(Boolean).length > 0 && (
                  <div className="flex items-start gap-3 rounded-2xl bg-muted/40 border border-white/5 p-3.5">
                    <Crown className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Directores</p>
                      <div className="flex flex-wrap gap-2">
                        {directores.filter(Boolean).map(u => u && (
                          <div key={u.id} className="flex items-center gap-1.5 bg-background/60 rounded-full pl-0.5 pr-3 py-0.5 border border-white/10">
                            <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[0.6rem] font-bold flex-shrink-0">
                              {u.nickname?.charAt(0) || '?'}
                            </div>
                            <span className="text-xs font-medium">{u.nickname || u.id}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {banda.info && (
                  <>
                    <div className="h-px w-full" style={{ background: `linear-gradient(90deg, ${color}, ${colorSec})` }} />
                    <div className="rounded-2xl bg-muted/30 border border-white/5 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <AlignLeft className="h-3.5 w-3.5 text-muted-foreground" />
                        <p className="text-[0.65rem] text-muted-foreground uppercase tracking-wider font-semibold">Información</p>
                      </div>
                      <div className="prose prose-sm dark:prose-invert text-muted-foreground max-w-none">
                        <ReactMarkdown>{banda.info}</ReactMarkdown>
                      </div>
                    </div>
                  </>
                )}
              </div>
           )}

           {activeTab === 'repertorio' && (() => {
              const filtered = repertorioSearch.trim()
                ? sortedRepertorio.filter(m => m.toLowerCase().includes(repertorioSearch.toLowerCase()))
                : sortedRepertorio;
              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[0.7rem] text-muted-foreground uppercase tracking-wider font-semibold flex items-center gap-2">
                      <ListMusic className="h-3.5 w-3.5" /> Repertorio · {sortedRepertorio.length} marchas
                    </p>
                  </div>
                  {sortedRepertorio.length > 0 ? (
                    <>
                      {/* Search input */}
                      <div className="relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <Input
                          value={repertorioSearch}
                          onChange={e => setRepertorioSearch(e.target.value)}
                          placeholder="Buscar marcha..."
                          className="pl-8 h-9 rounded-xl bg-muted/40 border-white/10 text-sm focus:border-white/25"
                        />
                        {repertorioSearch && (
                          <button
                            onClick={() => setRepertorioSearch('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      {/* Results */}
                      {filtered.length > 0 ? (
                        <div className="space-y-1 max-h-[55vh] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                          {filtered.map((marcha, index) => (
                            <div key={marcha} className="flex items-center gap-3 rounded-xl bg-muted/40 border border-white/5 px-3.5 py-2.5 hover:bg-muted/60 transition-colors">
                              <span className="text-[0.65rem] text-muted-foreground font-mono w-5 text-right flex-shrink-0">
                                {sortedRepertorio.indexOf(marcha) + 1}
                              </span>
                              <div className="h-3.5 w-[3px] rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                              <span className="text-sm font-medium flex-1 truncate">{marcha}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 border-2 border-dashed rounded-2xl">
                          <ListMusic className="mx-auto h-7 w-7 text-muted-foreground mb-2" />
                          <p className="text-muted-foreground text-sm">No se encontró "<strong>{repertorioSearch}</strong>"</p>
                          <button onClick={() => setRepertorioSearch('')} className="text-xs text-primary hover:underline mt-1">Limpiar búsqueda</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-10 border-2 border-dashed rounded-2xl">
                      <ListMusic className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-muted-foreground text-sm">Esta banda todavía no ha definido su repertorio.</p>
                    </div>
                  )}
                </div>
              );
           })()}

           {activeTab === 'posts' && isDirector && (
              <div className="space-y-4">
                  <div className='flex justify-between items-center'>
                    <h3 className="font-semibold text-lg flex items-center gap-2"><Newspaper className="h-5 w-5" /> Publicaciones ({posts.length})</h3>
                     <Dialog open={isCreatePostOpen} onOpenChange={setIsCreatePostOpen}>
                        <DialogTrigger asChild><Button size="sm">Crear Publicación</Button></DialogTrigger>
                        <DialogContent className="sm:max-w-2xl max-h-[90dvh] flex flex-col p-0">
                            <DialogHeader className="px-6 pt-6">
                                <DialogTitle>Nueva Publicación</DialogTitle>
                            </DialogHeader>
                            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
                                <CreatePostForm bandaId={banda.id} onFormSuccess={() => { setIsCreatePostOpen(false); onAction(); }} allHermandades={allHermandades} allBandas={allBandas}/>
                            </div>
                        </DialogContent>
                     </Dialog>
                  </div>
                  {posts.length > 0 ? (
                    posts.map(post => (
                      <div key={post.id} className="flex items-start gap-4 p-3 rounded-md border bg-muted/50">
                         <Image 
                            src={Array.isArray(post.mediaUrl) ? post.mediaUrl[0] : post.mediaUrl || ''} 
                            alt={post.title} 
                            width={80} 
                            height={80} 
                            className="rounded-md aspect-square object-cover" 
                          />
                        <div className="flex-grow">
                          <p className="font-semibold">{post.title}</p>
                          <div className="text-xs text-muted-foreground line-clamp-2 prose prose-sm dark:prose-invert max-w-none"><ReactMarkdown>{post.content}</ReactMarkdown></div>
                        </div>
                         <div className="flex flex-col gap-1">
                              <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" disabled={isSubmitting}><Trash2 className="h-4 w-4"/></Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                      <AlertDialogHeader>
                                          <AlertDialogTitle>¿Seguro que quieres eliminar esta publicación?</AlertDialogTitle>
                                          <AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                          <AlertDialogAction onClick={() => handleDeletePost(post.id)} className="bg-destructive hover:bg-destructive/80">
                                            Confirmar
                                          </AlertDialogAction>
                                      </AlertDialogFooter>
                                  </AlertDialogContent>
                              </AlertDialog>
                         </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm py-4">Esta banda aún no tiene publicaciones.</p>
                  )}
              </div>
           )}
           {activeTab === 'stories' && isDirector && (
               <div className="space-y-4">
                  <div className='flex justify-between items-center'>
                    <h3 className="font-semibold text-lg flex items-center gap-2"><ImageIcon className="h-5 w-5" /> Historias Activas ({stories.length})</h3>
                    <Dialog open={isCreateStoryOpen} onOpenChange={setIsCreateStoryOpen}>
                      <DialogTrigger asChild>
                         <Button size="sm">Añadir Historia</Button>
                      </DialogTrigger>
                      <DialogContent>
                         <DialogHeader>
                            <DialogTitle>Nueva Historia</DialogTitle>
                            <DialogDescription>Publica una imagen o vídeo que será visible durante 24 horas.</DialogDescription>
                         </DialogHeader>
                         <CreateStoryForm bandaId={banda.id} onFormSuccess={() => { setIsCreateStoryOpen(false); onAction(); }} />
                      </DialogContent>
                    </Dialog>
                  </div>
                  {stories.length > 0 ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {stories.map(story => {
                              let thumbnailUrl = "https://i.imgur.com/placeholder.png"; // Fallback image
                              if (story.mediaType === 'image' && story.mediaUrl) {
                                  thumbnailUrl = Array.isArray(story.mediaUrl) ? story.mediaUrl[0] : story.mediaUrl;
                              } else if (story.mediaType === 'video' && typeof story.mediaUrl === 'string') {
                                  const videoId = story.mediaUrl.split('/').pop()?.split('.')[0];
                                  if (videoId) {
                                      thumbnailUrl = `https://i.imgur.com/${videoId}h.jpg`;
                                  }
                              }
                              return (
                                <div key={story.id} className="relative aspect-[9/16] rounded-md overflow-hidden group">
                                    <Image src={thumbnailUrl} alt="Imagen de historia" fill className="object-cover" />
                                    <div className="absolute inset-0 bg-black/50 flex flex-col justify-between p-2">
                                        <p className="text-white text-xs">Expira en {Math.round((new Date(story.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60))}h</p>
                                        <div className="self-end z-20">
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button size="icon" variant="destructive" className="h-7 w-7" disabled={isSubmitting}>
                                                        <Trash2 className="h-4 w-4"/>
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>¿Eliminar esta historia?</AlertDialogTitle>
                                                        <AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => handleDeleteStory(story.id)} className="bg-destructive hover:bg-destructive/80">
                                                            Confirmar
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </div>
                                </div>
                          )})}
                      </div>
                  ) : (
                      <p className="text-muted-foreground text-sm py-4">Esta banda no tiene historias activas.</p>
                  )}
              </div>
           )}
           {activeTab === 'collaborations' && isDirector && (
              <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2"><Handshake className="h-5 w-5" /> Invitaciones Pendientes</h3>
                  {pendingCollaborations.length > 0 ? (
                      pendingCollaborations.map(post => (
                          <div key={post.id} className="flex items-center justify-between gap-3 text-sm p-3 rounded-md bg-muted/50 border">
                              <div className="flex items-center gap-3 flex-grow overflow-hidden">
                                  <Avatar className="h-10 w-10"><AvatarImage src={post.authorShieldUrl} /></Avatar>
                                  <div className='truncate'>
                                      <p className="font-semibold truncate">{post.authorName}</p>
                                      <p className="text-xs text-muted-foreground truncate">Te ha invitado a colaborar en: "{post.title}"</p>
                                  </div>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => handleCollaborationAction('reject', post.id)} disabled={isSubmitting}><X className="h-4 w-4" /></Button>
                                  <Button size="icon" variant="ghost" className="h-8 w-8 text-green-500" onClick={() => handleCollaborationAction('accept', post.id)} disabled={isSubmitting}><Check className="h-4 w-4" /></Button>
                              </div>
                          </div>
                      ))
                  ) : (
                      <p className="text-muted-foreground text-sm">No tienes invitaciones pendientes.</p>
                  )}
              </div>
            )}
            {activeTab === 'contracts' && isDirector && (
               <div className="space-y-6">
                  <div className="flex justify-between items-center">
                      <h3 className="font-semibold text-lg flex items-center gap-2"><FileSignature className="h-5 w-5" /> Contratos</h3>
                       <Dialog open={isCreateContractOpen} onOpenChange={setIsCreateContractOpen}>
                          <DialogTrigger asChild><Button size="sm">Crear Contrato</Button></DialogTrigger>
                          <DialogContent className="sm:max-w-2xl">
                              <DialogHeader>
                                  <DialogTitle>Emitir Nuevo Contrato</DialogTitle>
                                  <DialogDescription>
                                      Redacta un contrato para enviarlo a otra hermandad o banda.
                                  </DialogDescription>
                              </DialogHeader>
                              <CreateContractForm 
                                emitter={{id: banda.id, type: 'banda', name: banda.nombreCompleto}} 
                                allHermandades={allHermandades} 
                                allBandas={allBandas}
                                onFormSuccess={() => { setIsCreateContractOpen(false); onAction(); }}
                              />
                          </DialogContent>
                      </Dialog>
                  </div>
                   {isLoadingSent || isLoadingReceived ? <Loader2 className="h-6 w-6 animate-spin" /> : (
                      <>
                          <div>
                              <h4 className="font-medium text-md mb-2">Contratos Recibidos</h4>
                              <div className="space-y-2">
                                  {(receivedContracts || []).map(contract => (
                                    <div key={contract.id} className="p-2 border rounded-md hover:bg-muted/50">
                                      <button className="w-full text-left" onClick={() => setSelectedContract(contract)}>
                                          <p className="font-semibold">{contract.concept}</p>
                                          <p className="text-xs text-muted-foreground">De: {contract.emitterName}</p>
                                          <p className={`text-xs font-bold ${contract.status === 'signed' ? 'text-green-500' : 'text-yellow-500'}`}>
                                              {contract.status.toUpperCase()}
                                          </p>
                                      </button>
                                    </div>
                                  ))}
                                  {(receivedContracts || []).length === 0 && <p className="text-xs text-muted-foreground">No has recibido contratos.</p>}
                              </div>
                          </div>
                           <div>
                              <h4 className="font-medium text-md mb-2">Contratos Enviados</h4>
                              <div className="space-y-2">
                                   {(sentContracts || []).map(contract => (
                                    <div key={contract.id} className="p-2 border rounded-md hover:bg-muted/50">
                                      <button className="w-full text-left" onClick={() => setSelectedContract(contract)}>
                                          <p className="font-semibold">{contract.concept}</p>
                                          <p className="text-xs text-muted-foreground">Para: {contract.recipientName}</p>
                                          <p className={`text-xs font-bold ${contract.status === 'signed' ? 'text-green-500' : 'text-yellow-500'}`}>
                                              {contract.status.toUpperCase()}
                                          </p>
                                      </button>
                                    </div>
                                  ))}
                                  {(sentContracts || []).length === 0 && <p className="text-xs text-muted-foreground">No has enviado contratos.</p>}
                              </div>
                          </div>
                      </>
                   )}
               </div>
           )}
            {activeTab === 'destacadas' && isDirector && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h3 className="font-semibold text-lg flex items-center gap-2"><Star className="h-5 w-5" /> Historias Destacadas</h3>
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button size="sm">Crear Grupo</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Crear Grupo de Historias Destacadas</DialogTitle>
                                    <DialogDescription>Agrupa tus mejores momentos.</DialogDescription>
                                </DialogHeader>
                                <CreateHighlightGroupForm banda={banda} onFormSuccess={onAction} />
                            </DialogContent>
                        </Dialog>
                    </div>
                     {banda.highlightGroups && banda.highlightGroups.length > 0 ? (
                        banda.highlightGroups.map(group => (
                            <div key={group.id} className="flex items-center justify-between p-2 border rounded-md">
                                <div className="flex items-center gap-3">
                                    <Avatar>
                                        <AvatarImage src={group.coverUrl} />
                                    </Avatar>
                                    <p className="font-semibold">{group.name} ({group.storyIds.length} historias)</p>
                                </div>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-destructive h-8 w-8">
                                      <Trash2 className="h-4 w-4"/>
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>¿Eliminar este grupo de destacadas?</AlertDialogTitle>
                                      <AlertDialogDescription>Esta acción es permanente.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeleteHighlight(group.id)} className="bg-destructive hover:bg-destructive/80">
                                        Confirmar
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-muted-foreground">Crea y gestiona los grupos de historias destacadas que aparecerán en tu perfil.</p>
                    )}
                </div>
            )}
            {activeTab === 'fijadas' && isDirector && (
                <div className="space-y-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2"><Pin className="h-5 w-5" /> Publicaciones Fijadas</h3>
                     <p className="text-sm text-muted-foreground">Selecciona hasta 3 publicaciones para que aparezcan fijadas en tu perfil.</p>
                     <PinPostsForm banda={banda} posts={posts} onFormSuccess={onAction} />
                </div>
            )}
            {activeTab === 'settings' && isDirector && (
                 <div className="space-y-4">
                    <h3 className="font-semibold text-lg flex items-center gap-2"><Settings className="h-5 w-5" /> Ajustes de la Banda</h3>
                    <SettingsManager banda={banda} onActionSuccess={onAction} />
                 </div>
            )}

           </div>
        </ScrollArea>
       
       {selectedContract && (
           <Dialog open={!!selectedContract} onOpenChange={(open) => !open && setSelectedContract(null)}>
              <DialogContent className="w-full max-w-md h-auto max-h-[90vh] p-0">
                   <ContractCard contract={selectedContract} perspective="recipient" isDetailView />
                   {selectedContract.recipientId === `banda:${banda.id}` && selectedContract.status === 'pending' && (
                      <DialogFooter className="border-t p-4">
                         <Button variant="destructive" onClick={() => { handleRejectContract(selectedContract.id); setSelectedContract(null); }} disabled={isSubmitting}>Rechazar</Button>
                         <Button onClick={() => { handleSignContract(selectedContract.id); setSelectedContract(null); }} disabled={isSubmitting}>Firmar Contrato</Button>
                      </DialogFooter>
                   )}
              </DialogContent>
           </Dialog>
       )}
    </SheetContent>
  </Sheet>
);
}


interface BandasClientProps {
    initialBandas: Banda[];
    allUserProfiles: Record<string, UserProfile>;
    currentUser: { uid: string } | null;
    allHermandades: JartinetHermandad[];
    allPosts: SocialPost[];
    initialStories: Record<string, SocialStory[]>;
}

export function BandasClient({ 
    initialBandas, 
    allUserProfiles,
    currentUser,
    allHermandades,
    allPosts,
    initialStories,
}: BandasClientProps) {
  const [selectedBanda, setSelectedBanda] = useState<Banda | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { refetch } = useAuth();
  
  const handleActionCompletion = () => {
    refetch();
  };
  
  const handleBandaClick = (banda: Banda) => {
    setSelectedBanda(banda);
    setIsSheetOpen(true);
  };
  
  const hexToRgba = (hex: string, alpha: number) => {
    if (!hex || hex.length < 7) hex = '#000000';
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const tiposDeBanda = ['Agrupación Musical', 'Cornetas y Tambores', 'Banda de Música', 'Capilla'];
  const bandasPorTipo = tiposDeBanda.map(tipo => ({
    tipo,
    bandas: initialBandas.filter(b => b.tipo === tipo)
  })).filter(g => g.bandas.length > 0);
  
  const getStoriesForBanda = (bandaId: string) => {
      const key = `banda:${bandaId}`;
      return initialStories[key] || [];
  };

  return (
    <div className="space-y-8">
      <div>
          <h1 className="font-headline text-5xl font-bold text-primary">
            Bandas
          </h1>
          <p className="mt-2 text-lg text-muted-foreground">
            Consulta las formaciones musicales del servidor.
          </p>
      </div>
      
      {initialBandas.length > 0 ? (
        <div className="space-y-12">
          {bandasPorTipo.map(({ tipo, bandas }) => (
            <div key={tipo} className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <h2 className="font-headline text-3xl font-bold text-primary mb-6 flex items-center gap-4">
                {tipo}
                <span className="h-[2px] flex-1 bg-gradient-to-r from-primary/50 to-transparent rounded-full" />
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {bandas.map(banda => {
                  const color = banda.colorPrimario || '#9b8854';
                  const colorSec = banda.colorSecundario || color;
                  return (
                    <motion.div
                      key={banda.id}
                      whileHover={{ y: -7 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleBandaClick(banda)}
                      className="group relative cursor-pointer"
                      transition={{ type: 'spring', stiffness: 280, damping: 20 }}
                    >
                      <div className="relative overflow-hidden rounded-2xl border border-white/8 min-h-[200px] flex flex-col transition-all duration-300 shadow-md group-hover:shadow-[0_10px_40px_rgba(0,0,0,0.5)] group-hover:border-white/20">
                        {/* Full bleed photo background */}
                        <div className="absolute inset-0">
                          <div
                            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                            style={{ backgroundImage: `url('${banda.foto || banda.escudoUrl}')` }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />
                          <div
                            className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-opacity duration-500"
                            style={{ background: `linear-gradient(145deg, ${color}, transparent 60%)` }}
                          />
                        </div>

                        {/* Bottom color bar */}
                        <div
                          className="absolute bottom-0 left-0 right-0 h-[3px] rounded-b-2xl"
                          style={{ background: `linear-gradient(90deg, ${color}, ${colorSec})` }}
                        />

                        {/* Content */}
                        <div className="relative z-10 flex flex-col flex-1 p-3.5 justify-between">
                          {/* Top: tipo badge */}
                          <div>
                            <span className="text-[0.62rem] font-semibold px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm border border-white/15 text-white/75">
                              {banda.tipo}
                            </span>
                          </div>

                          {/* Bottom: escudo + name */}
                          <div className="flex items-end gap-2.5 mt-6">
                            <div className="relative flex-shrink-0">
                              <div
                                className="absolute inset-0 blur-lg rounded-full scale-150 opacity-0 group-hover:opacity-60 transition-opacity duration-500"
                                style={{ backgroundColor: color }}
                              />
                              <div className="relative rounded-full p-1 border border-white/20 bg-black/40 backdrop-blur-sm">
                                <Image
                                  src={banda.escudoUrl}
                                  alt={banda.nombreCompleto}
                                  width={44}
                                  height={44}
                                  className="h-11 w-11 object-contain drop-shadow-lg transition-transform duration-400 group-hover:-translate-y-0.5"
                                />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0 pb-0.5">
                              <p className="font-bold text-white leading-tight text-sm line-clamp-2 drop-shadow-md group-hover:text-primary transition-colors duration-300">
                                {banda.nombreCompleto}
                              </p>
                              {banda.componentes && (
                                <p className="text-[0.62rem] text-white/50 mt-0.5">{banda.componentes} comp.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
         <div className="text-center py-16 border-2 border-dashed rounded-lg">
            <Music className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-semibold">No hay bandas registradas</h3>
            <p className="mt-1 text-sm text-muted-foreground">Los datos de las bandas no se pudieron cargar. Contacta con un administrador.</p>
        </div>
      )}

      {selectedBanda && (
        <BandaDetail 
            banda={selectedBanda} 
            open={isSheetOpen} 
            onOpenChange={setIsSheetOpen} 
            allUserProfiles={allUserProfiles}
            currentUser={currentUser}
            onAction={handleActionCompletion}
            allHermandades={allHermandades}
            allBandas={initialBandas}
            allPosts={allPosts}
            stories={selectedBanda ? getStoriesForBanda(selectedBanda.id) : []}
        />
      )}
    </div>
  );
}
